import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { sortedModelsByWeeklyScores } from "../utils/sortedModels";
import { scoreWeeklyFromDailyModelSeries } from "../scores/utils/scoreWeeklyFromDailyModelSeries";
import { format, isBefore, isSameDay, startOfDay } from "date-fns";
import { Model } from "../../types/models";
import { parseLocalDateString } from "../utils/date/calendarDate";
import { selectAuthSubscriptionPlan } from "./auth";
import { isModelAvailableForSubscriptionPlan } from "../../utils/isModelAvailableForSubscriptionPlan";
import { shouldShowModelInDailyList } from "../../utils/shouldShowModelInDailyList";

export const selectModelLoading = (state: RootState) => state.models.loading;
export const selectModelsLoadError = (state: RootState) => state.models.loadError;
export const selectModelsState = (state: RootState) => state.models.items;

export const selectActiveModelsState = createSelector(
  selectModelsState,
  (items) =>
    items?.filter((model) => model.is_active && model.is_active_user && !model.deleted_at) ?? null,
);

export const selectInactiveModelsState = createSelector(
  selectModelsState,
  (items) =>
    items?.filter((model) => model.is_active && !model.is_active_user && !model.deleted_at) ?? null,
);

export const selectActiveModelsForSubscriptionPlanState = createSelector(
  selectModelsState,
  selectAuthSubscriptionPlan,
  (items, plan) =>
    items?.filter(
      (model) =>
        model.is_active &&
        model.is_active_user &&
        !model.deleted_at &&
        isModelAvailableForSubscriptionPlan(model, plan),
    ) ?? null,
);

export const selectInactiveModelsForSubscriptionPlanState = createSelector(
  selectModelsState,
  selectAuthSubscriptionPlan,
  (items, plan) =>
    items?.filter(
      (model) =>
        model.is_active &&
        !model.is_active_user &&
        !model.deleted_at &&
        isModelAvailableForSubscriptionPlan(model, plan),
    ) ?? null,
);

/** Models present in catalog but not included in the user's plan (e.g. Pro on Essential). */
export const selectModelsOutsideSubscriptionPlanState = createSelector(
  selectModelsState,
  selectAuthSubscriptionPlan,
  (items, plan) =>
    items?.filter(
      (model) =>
        model.is_active &&
        !model.deleted_at &&
        !isModelAvailableForSubscriptionPlan(model, plan),
    ) ?? null,
);

/** User-enabled models included in the current subscription plan. */
export const selectActiveModelsCount = createSelector(
  selectActiveModelsForSubscriptionPlanState,
  (models) => models?.length ?? 0,
);

/** Catalog models available on the current subscription plan (system-active, in-plan). */
export const selectTotalModelsCount = createSelector(
  selectModelsState,
  selectAuthSubscriptionPlan,
  (items, plan) =>
    items?.filter(
      (model) =>
        model.is_active &&
        !model.deleted_at &&
        isModelAvailableForSubscriptionPlan(model, plan),
    ).length ?? 0,
);

export const selectActiveByDateModelsState = (date: string | undefined) =>
  createSelector(
    (state: RootState) => state.models.items,
    (models) => {
      if (!models || !date) return null;

      const selectedDate = parseLocalDateString(date);
      if (Number.isNaN(selectedDate.getTime())) return null;

      return models.filter((model: Model) => {
        if (!model || !model.is_active_user) return false;

        const modelCreatedDate = startOfDay(new Date(model.created_at));
        const modelDeletedDate = model.deleted_at ? startOfDay(new Date(model.deleted_at)) : null;
        const isAfterCreated = isSameDay(selectedDate, modelCreatedDate) || !isBefore(selectedDate, modelCreatedDate);
        const isBeforeDeleted = !modelDeletedDate || isSameDay(selectedDate, modelDeletedDate) || isBefore(selectedDate, modelDeletedDate);

        return isAfterCreated && isBeforeDeleted;
      });
    },
  );

export const selectByDateModelsSortedByDailyAverageScoresState = (brandId: number, date: string | undefined) =>
  createSelector(
    (state: RootState) => state.models.items,
    (state: RootState) => state.scores.dailyAverageModel.data,
    selectAuthSubscriptionPlan,
    (models, dailyScoresData, plan) => {
      if (!models || !date || !dailyScoresData) return null;

      const modelsByDate = models.filter(
        (model) => model.is_active && model.is_active_user && !model.deleted_at,
      );
      const modelsWithScores = modelsByDate
        .map((model) => {
          const modelScores = dailyScoresData[String(model.id)];
          const brandScores = modelScores?.[String(brandId)];

          const scoreForDate = brandScores?.scores?.find(
            (score) => format(score.created_at, "yyyy-MM-dd") === date,
          );

          return {
            ...model,
            dailyScore: scoreForDate?.visibility || 0,
            hasScoreData: scoreForDate != null,
          };
        })
        .filter((model) => shouldShowModelInDailyList(model, model.hasScoreData, plan));

      return modelsWithScores.sort((a, b) => b.dailyScore - a.dailyScore);
    },
  );

export const selectActiveModelsByBrandState = (id: string | undefined) =>
  createSelector(
    (state: RootState) => state.models.items,
    (state: RootState) => state.scores.dailyAverageModel.data,
    (models, dailyByModel) => {
      if (!models || !id || !dailyByModel) return null;

      const brandId = Number(id);

      const activeModels = models
        .filter((model) => model && model.is_active && model.is_active_user && !model.deleted_at)
        .map((model) => {
          const item = dailyByModel[String(model.id)]?.[String(brandId)];
          const weeklyScore = scoreWeeklyFromDailyModelSeries(
            brandId,
            Number(model.id),
            item,
          );

          return {
            ...model,
            title: `${model.name} ${model.data.version}`,
            weeklyScores: weeklyScore,
          };
        });

      return sortedModelsByWeeklyScores(activeModels);
    },
  );